import { SHIP, WEATHER, smoothstep, terrainHeight } from "@tiny-seas/shared";

/**
 * The game's ocean waves. One parameter set drives the water shader (`Environment` uploads its
 * components as uniforms) and the CPU sampling ships ride on, so the rendered surface and hull
 * motion agree. Purely visual: the server and ship physics never read waves.
 *
 * Two models share one surface definition, a sum of directional components:
 * - `sines`: six hand-tuned sines, vertical only (the original ocean).
 * - `gerstner`: wind-driven Gerstner (trochoidal) waves weighted by a JONSWAP spectrum. Points
 *   orbit horizontally as well, which sharpens crests and flattens troughs; where the surface
 *   compresses (Jacobian below 1) the shader draws whitecaps. The components' wavenumbers,
 *   directions and phases are fixed; the wind only changes their heights (`blowWind`), so the
 *   room's weather can change the sea continuously without waves jumping.
 */

export type WaveModel = "sines" | "gerstner";

/** One hand-tuned sine: `sin(kx·x + kz·z + omega·t) · height`, then scaled by `amplitude`. */
export interface Swell { kx: number; kz: number; height: number; omega: number }
/** One value-noise normal layer: feature frequency (1/m), slope strength and drift-speed multiplier. */
export interface Ripple { scale: number; strength: number; speed: number }
/** A smoothstep from `start` to `end`, times `amount`. */
export interface Band { start: number; end: number; amount: number }

/**
 * Waves meeting land. Swell flattens over `shoalDepth`; inside `surfDepth` breakers roll in
 * along the depth contours (so they always face the beach), raise the water and run up the
 * shore; foam breaks on their crests and rings sea rocks in step with them.
 */
export interface ShoreParams {
  /** Water depth (m) below which open-sea swell starts to flatten, gone at the shoreline. */
  shoalDepth: number;
  /** Water depth (m) where the surf zone begins. */
  surfDepth: number;
  /** Metres of depth between successive breakers (closer on steep shores). */
  surfSpacing: number;
  /** Seconds between breakers. */
  surfPeriod: number;
  /** Breaker height, m. */
  surfHeight: number;
  /** Breaking-crest foam amount. */
  breakerFoam: number;
  /** Shallowest swash-line depth, m, and how far it swings with each breaker. */
  washBase: number;
  washRange: number;
}

/**
 * Ships and the sea around them: hulls displace water in a linear deep-water wave simulation
 * (`scene/water-sim.ts`), so bow waves, Kelvin wakes and slam rings come from the water itself;
 * foam forms where those waves break and along the hull; spray leaves water jetting upwards.
 */
export interface WakeParams {
  /** Hull pressure on the sea as a fraction of its hydrostatic weight (1 holds the water down by the hull's immersed
   *  depth). The linear sea overreacts to the full load near a hull, so the game uses about half. */
  displacement: number;
  /** Wave damping, 1/s (short waves damp faster). */
  damping: number;
  /** Foam where simulated waves break (downward acceleration beyond g/2). */
  breaking: number;
  /** Foam churned along the hull per second at full speed (boundary-layer turbulence). */
  churn: number;
  /** Seconds for foam to fade to 37%. */
  lifetime: number;
  /** How fast foam spreads sideways (0 stays put … 1 spreads quickly). */
  spread: number;
  /** Spray thrown off water jetting upwards, ×. */
  spray: number;
}

/** Hull response to the swell: natural periods (s) and damping ratio of a mass on its waterplane spring. */
export interface HullParams {
  heavePeriod: number;
  pitchPeriod: number;
  rollPeriod: number;
  /** Fraction of critical damping. */
  damping: number;
}

export interface SinesParams {
  /** Metres of displacement per unit of raw swell height. */
  amplitude: number;
  /** Exactly six. */
  swells: Swell[];
  /** Over raw swell height. */
  crestTint: Band;
  /** Over raw swell height. */
  crestFoam: Band;
}

export interface GerstnerParams {
  /** Wind speed 10 m above the sea, m/s: sets the peak wavelength and the sea's height. */
  windSpeed: number;
  /** Compass direction the wind (and the waves) travel towards, degrees (0° = +z, 90° = +x). */
  windHeading: number;
  /** Centre of the components' fixed directions, degrees. */
  prevailingHeading: number;
  /** How far either side of `prevailingHeading` the wind can veer and still find waves, degrees. */
  headingRange: number;
  /** Strongest wind the components resolve, m/s: sets the longest component. */
  maxWindSpeed: number;
  /** Open water the wind has blown over, km: longer fetch grows longer, higher waves. */
  fetch: number;
  /** Directional spreading exponent s in cos^2s: higher keeps waves closer to the wind. */
  spread: number;
  /** 0 rolls waves as sines; 1 gives full circular orbits (sharp crests, broad troughs). */
  choppiness: number;
  /** Multiplies every component's height. */
  heightScale: number;
  /** Shortest component, m. Components under `MESH_WAVELENGTH` only shade normals. */
  shortestWavelength: number;
  /** Random directions and phases. */
  seed: number;
  /** Over height divided by the highest possible crest (0…1). */
  crestTint: Band;
  /** Over surface compression, 1 − Jacobian (0 flat … 1 folding). */
  crestFoam: Band;
}

export interface WaveParams {
  model: WaveModel;
  sines: SinesParams;
  gerstner: GerstnerParams;
  /** Swell slope kept at 900 m and beyond (1 at 200 m). */
  farSlope: number;
  /** Coarse, medium and fine wind ripples (normals only), gone by 650, 350 and 110 m. */
  ripples: Ripple[];
  shore: ShoreParams;
  wake: WakeParams;
  hull: HullParams;
}

export const GAME_WAVES: Readonly<WaveParams> = {
  model: "gerstner",
  sines: {
    amplitude: 2,
    swells: [
      { kx: 0.11, kz: 0, height: 0.45, omega: 0.9 },
      { kx: 0, kz: 0.07, height: 0.55, omega: -0.7 },
      { kx: 0.15, kz: 0.15, height: 0.2, omega: 1.3 },
      { kx: -0.083, kz: 0.121, height: 0.28, omega: 1.1 },
      { kx: 0.19, kz: 0.062, height: 0.16, omega: -1.5 },
      { kx: 0.047, kz: -0.26, height: 0.11, omega: 1.9 },
    ],
    crestTint: { start: 0.5, end: 1.35, amount: 1 },
    crestFoam: { start: 1.15, end: 1.55, amount: 0.12 },
  },
  gerstner: {
    windSpeed: 12,
    windHeading: 60,
    prevailingHeading: 60,
    headingRange: WEATHER.headingRange,
    maxWindSpeed: WEATHER.windMax,
    fetch: 150,
    spread: 6,
    choppiness: 0.8,
    heightScale: 1,
    shortestWavelength: 3,
    seed: 7,
    crestTint: { start: 0.2, end: 0.7, amount: 1 },
    // Compression at 12 m/s: median 0, 90th percentile ≈ 0.14, 99th ≈ 0.25.
    crestFoam: { start: 0.12, end: 0.3, amount: 0.6 },
  },
  farSlope: 0.45,
  ripples: [{ scale: 0.65, strength: 0.13, speed: 1 }, { scale: 1.65, strength: 0.09, speed: 1 }, { scale: 4.2, strength: 0.045, speed: 1 }],
  shore: { shoalDepth: 6, surfDepth: 5, surfSpacing: 1.6, surfPeriod: 7, surfHeight: 0.6, breakerFoam: 0.8, washBase: 0.14, washRange: 0.32 },
  wake: { displacement: 1.2, damping: 0.02, breaking: 3, churn: 1.5, lifetime: 45, spread: 0.6, spray: 2 },
  hull: { heavePeriod: 2.95, pitchPeriod: 3.2, rollPeriod: 6.2, damping: 0.3 },
};

/** Water shader array size: the most components either model can produce. */
export const WAVE_COMPONENTS = 32;
/** Components shorter than four 2 m grid cells would alias as geometry; they only shade normals. */
export const MESH_WAVELENGTH = 8;
/** Reduced motion freezes the waves at this fraction of their height and keeps hulls level. */
export const REDUCED_MOTION_WAVES = 0.4;
const GRAVITY = 9.81;

export function cloneWaves(p: Readonly<WaveParams>): WaveParams {
  const band = (b: Band): Band => ({ ...b });
  return {
    model: p.model,
    sines: { amplitude: p.sines.amplitude, swells: p.sines.swells.map(s => ({ ...s })), crestTint: band(p.sines.crestTint), crestFoam: band(p.sines.crestFoam) },
    gerstner: { ...p.gerstner, crestTint: band(p.gerstner.crestTint), crestFoam: band(p.gerstner.crestFoam) },
    farSlope: p.farSlope, ripples: p.ripples.map(r => ({ ...r })), shore: { ...p.shore }, wake: { ...p.wake }, hull: { ...p.hull },
  };
}

/**
 * One surface component at rest position (x, z): phase θ = kx·x + kz·z + omega·t + phase;
 * height `amplitude · sin θ`; horizontal offset `lateral · k̂ · cos θ`.
 */
export interface WaveComponent {
  kx: number; kz: number; omega: number; phase: number;
  /** Metres. */
  amplitude: number;
  /** Horizontal orbit radius, m (0 for sines). */
  lateral: number;
  /** Displaces the rendered grid; otherwise normals only. */
  mesh: boolean;
}

/**
 * Gerstner components' fixed directions (compass radians) and frequency bin widths (rad/s), by
 * index, and `blowWind`'s per-component scratch.
 */
export interface WindBank { headings: Float64Array; widths: Float64Array; energy: Float64Array; aimed: Float64Array }

/** Everything the shader and CPU sampling need, derived from `WaveParams`. */
export interface WaveSurface {
  components: WaveComponent[];
  /** `vWave` = height / bandScale: raw swell height for sines, height / max crest for Gerstner. */
  bandScale: number;
  crestTint: Band;
  crestFoam: Band;
  /** Crest foam follows compression (Gerstner) rather than height (sines). */
  foamFromCompression: boolean;
  /** What `blowWind` reweights; null for sines. */
  wind: WindBank | null;
}

/** The shader stops at the first component too short for the grid or the pixel, so components come longest first. */
const longestFirst = (a: WaveComponent, b: WaveComponent): number => Math.hypot(a.kx, a.kz) - Math.hypot(b.kx, b.kz);

export function waveSurface(p: Readonly<WaveParams>): WaveSurface {
  if (p.model === "sines") {
    const s = p.sines;
    return {
      components: s.swells.map(w => ({ kx: w.kx, kz: w.kz, omega: w.omega, phase: 0, amplitude: w.height * s.amplitude, lateral: 0, mesh: true }))
        .sort(longestFirst),
      bandScale: s.amplitude || 1, crestTint: s.crestTint, crestFoam: s.crestFoam, foamFromCompression: false, wind: null,
    };
  }
  const surface = gerstnerBank(p.gerstner);
  blowWind(surface, p.gerstner);
  return surface;
}

/** Deterministic [0, 1) generator (mulberry32). */
function random(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** JONSWAP peak angular frequency (rad/s) for wind speed (m/s) and fetch (km). */
export function peakFrequency(windSpeed: number, fetch: number): number {
  return 22 * Math.cbrt(GRAVITY * GRAVITY / (Math.max(windSpeed, 0.5) * Math.max(fetch, 0.1) * 1000));
}

/**
 * `WAVE_COMPONENTS` fixed components with zero height: log-spaced frequency bins from half the
 * peak of `maxWindSpeed` up to `shortestWavelength` (so ascending frequency is longest first), each
 * one jittered frequency, a random phase and deep-water dispersion ω² = g·k. Directions stride
 * by the golden ratio across the veer range widened by the spreading lobe's reach (where cos^2s
 * falls to 0.1), so neighbouring bins point apart and every heading in range finds waves at
 * every scale.
 */
function gerstnerBank(g: Readonly<GerstnerParams>): WaveSurface {
  const rand = random(g.seed);
  const low = peakFrequency(g.maxWindSpeed, g.fetch) * 0.5, high = Math.max(low * 1.01, Math.sqrt(GRAVITY * 2 * Math.PI / Math.max(g.shortestWavelength, 0.5)));
  const ratio = (high / low) ** (1 / WAVE_COMPONENTS), centre = g.prevailingHeading * Math.PI / 180;
  const reach = Math.min(Math.PI, Math.max(g.headingRange, 0) * Math.PI / 180 + Math.acos(0.1 ** (1 / (2 * Math.max(g.spread, 0.5)))));
  const components: WaveComponent[] = [], headings = new Float64Array(WAVE_COMPONENTS), widths = new Float64Array(WAVE_COMPONENTS);
  const stride = rand();
  for (let i = 0; i < WAVE_COMPONENTS; i++) {
    const lo = low * ratio ** i, hi = lo * ratio, w = lo + (hi - lo) * rand();
    const heading = centre + reach * (2 * ((stride + i * 0.6180339887498949) % 1) - 1), k = w * w / GRAVITY;
    // Phase k·x + ωt with ω > 0 travels along −k̂, so k points against the heading.
    components.push({ kx: -k * Math.sin(heading), kz: -k * Math.cos(heading), omega: w, phase: rand() * 2 * Math.PI,
      amplitude: 0, lateral: 0, mesh: 2 * Math.PI / k >= MESH_WAVELENGTH });
    headings[i] = heading;
    widths[i] = hi - lo;
  }
  return {
    components, bandScale: 1, crestTint: g.crestTint, crestFoam: g.crestFoam, foamFromCompression: true,
    wind: { headings, widths, energy: new Float64Array(WAVE_COMPONENTS), aimed: new Float64Array(WAVE_COMPONENTS) },
  };
}

/** Neighbouring bins either side that share a bin's energy when it points off the wind. */
const SHARE_BINS = 2;
/**
 * Directional weight every component keeps however far off the wind it points: a cross sea.
 * Without it a veer range leaves only a few components carrying the sea, in parallel ranks.
 */
const CROSS_SEA = 0.25;

/**
 * Sets the Gerstner components' heights for `g`'s wind in place. Each bin's JONSWAP energy is
 * weighted by cos^2s of its angle off the wind above a `CROSS_SEA` floor; within each run of neighbouring bins the weighted
 * energy is scaled back up to the run's full energy, so the spectrum's shape (and its peak)
 * survives whichever way the wind blows, then the whole sea is scaled to the spectrum's total.
 * Only heights (and the orbits that follow them) change, so the surface changes continuously
 * as the wind does. Sines are left alone.
 */
export function blowWind(surface: WaveSurface, g: Readonly<GerstnerParams>): void {
  const bank = surface.wind;
  if (!bank) return;
  const U = Math.max(g.windSpeed, 0.5), F = Math.max(g.fetch, 0.1) * 1000;
  const peak = peakFrequency(g.windSpeed, g.fetch), alpha = 0.076 * Math.pow(U * U / (F * GRAVITY), 0.22);
  const wind = g.windHeading * Math.PI / 180, spread = 2 * Math.max(g.spread, 0.5), components = surface.components;
  const { energy, aimed } = bank, n = components.length;
  let full = 0;
  for (let i = 0; i < n; i++) {
    const w = components[i]!.omega, sigma = w <= peak ? 0.07 : 0.09;
    const r = Math.exp(-((w - peak) ** 2) / (2 * sigma * sigma * peak * peak));
    energy[i] = 2 * alpha * GRAVITY * GRAVITY / w ** 5 * Math.exp(-1.25 * (peak / w) ** 4) * 3.3 ** r * bank.widths[i]!;
    const along = Math.cos(bank.headings[i]! - wind);
    aimed[i] = energy[i]! * (CROSS_SEA + (1 - CROSS_SEA) * (along > 0 ? along ** spread : 0));
    full += energy[i]!;
  }
  let shared = 0;
  for (let i = 0; i < n; i++) {
    let run = 0, runAimed = 0;
    for (let j = Math.max(0, i - SHARE_BINS); j <= Math.min(n - 1, i + SHARE_BINS); j++) { run += energy[j]!; runAimed += aimed[j]!; }
    // Held as energy until the gain is known.
    components[i]!.amplitude = runAimed > 0 ? aimed[i]! * run / runAimed : 0;
    shared += components[i]!.amplitude;
  }
  const gain = shared > 0 ? full / shared : 0;
  let slopeSum = 0, heights = 0;
  for (const c of components) {
    c.amplitude = Math.sqrt(c.amplitude * gain) * g.heightScale;
    slopeSum += c.amplitude * Math.hypot(c.kx, c.kz);
    heights += c.amplitude;
  }
  // Σ lateral·k ≤ 1 keeps the surface from looping over itself at any phase alignment.
  const lateral = g.choppiness * Math.min(1, 1 / Math.max(slopeSum, 1e-6));
  for (const c of components) c.lateral = c.amplitude * lateral;
  surface.bandScale = heights || 1;
}

let offsetX = 0, offsetZ = 0;
/** Horizontal offset of the rest point (x, z) into `offsetX`/`offsetZ`. */
function lateralOffset(components: readonly WaveComponent[], scale: number, x: number, z: number, t: number, meshOnly: boolean): void {
  offsetX = offsetZ = 0;
  for (const c of components) {
    if (c.lateral === 0 || (meshOnly && !c.mesh)) continue;
    const k = Math.hypot(c.kx, c.kz), r = c.lateral * scale * Math.cos(x * c.kx + z * c.kz + t * c.omega + c.phase) / k;
    offsetX += c.kx * r;
    offsetZ += c.kz * r;
  }
}

/**
 * Water height (m) at world (x, z): finds the rest point whose displaced position lands on
 * (x, z) by fixed-point iteration (exact for sines), then sums the heights there. `meshOnly`
 * samples what the displaced grid renders.
 */
export function surfaceHeight(components: readonly WaveComponent[], scale: number, x: number, z: number, t: number, meshOnly = false): number {
  let px = x, pz = z;
  if (components.some(c => c.lateral !== 0)) {
    for (let i = 0; i < 4; i++) {
      lateralOffset(components, scale, px, pz, t, meshOnly);
      px = x - offsetX;
      pz = z - offsetZ;
    }
  }
  let h = 0;
  for (const c of components) if (!meshOnly || c.mesh) h += Math.sin(px * c.kx + pz * c.kz + t * c.omega + c.phase) * c.amplitude;
  return h * scale;
}

const fract = (v: number): number => v - Math.floor(v);
function hash12(x: number, y: number): number {
  let a = fract(x * 0.1031), b = fract(y * 0.1031), c = fract(x * 0.1031);
  const d = a * (b + 33.33) + b * (c + 33.33) + c * (a + 33.33);
  a += d; b += d; c += d;
  return fract((a + b) * c);
}
/** The water shader's `vnoise` (float64 here, so values differ from the GPU in the last bits). */
export function valueNoise(x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hash12(ix, iy), b = hash12(ix + 1, iy), c = hash12(ix, iy + 1), d = hash12(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

/** Breaker phase: crests sit on depth contours and move shoreward as `t` grows. */
export function surfPhase(shore: Readonly<ShoreParams>, x: number, z: number, depth: number, t: number): number {
  return depth * 2 * Math.PI / shore.surfSpacing + t * 2 * Math.PI / shore.surfPeriod + valueNoise(x * 0.045, z * 0.045) * 6.28;
}

/** 0 offshore of `surfDepth`, rising to 1 near the beach, gone on dry land. */
export function surfZone(shore: Readonly<ShoreParams>, depth: number): number {
  return (1 - smoothstep(shore.surfDepth * 0.35, shore.surfDepth, depth)) * smoothstep(-0.3, 0.8, depth);
}

/** Breaker rise (m) above the mean water: peaked crests, `E[(½ + ½ sin θ)³] = 5/16` removed. */
export function surfLift(shore: Readonly<ShoreParams>, x: number, z: number, depth: number, t: number): number {
  const s = 0.5 + 0.5 * Math.sin(surfPhase(shore, x, z, depth, t));
  return shore.surfHeight * surfZone(shore, depth) * (s * s * s - 0.3125);
}

/** Open-sea swell kept at `depth` (m): full offshore, gone at the shoreline. */
export function shoaling(shore: Readonly<ShoreParams>, depth: number): number {
  return smoothstep(0, shore.shoalDepth, depth);
}

/**
 * Water height (m) at world (x, z) including the coast: swell flattened over shallow water
 * plus breakers, with depth from the shared terrain (the shader samples its baked shore map).
 */
export function waterHeight(components: readonly WaveComponent[], shore: Readonly<ShoreParams>, scale: number, x: number, z: number, t: number, meshOnly = false): number {
  const depth = -terrainHeight(x, z);
  return surfaceHeight(components, scale, x, z, t, meshOnly) * shoaling(shore, depth) + surfLift(shore, x, z, depth, t) * scale;
}

/** Hull pose on the waves: rise (m), and Babylon `rotation.x`/`rotation.z` (rad) in the ship's frame. */
export interface ShipMotion { heave: number; pitch: number; roll: number }

/** Bow/stern and port/starboard probe offsets: the hull rides the water under its own footprint. */
const HALF_LENGTH = SHIP.length * 0.4, HALF_BEAM = SHIP.beam / 2;

/**
 * How a hull at (x, z) facing `heading` sits on the rendered surface at shader time `t`: heave
 * is the mean height under the centre, bow, stern and both sides; pitch and roll follow the
 * surface between bow and stern and between the sides. With `shore`, it rides the coastal
 * water (`waterHeight`), so docked hulls settle in the shallows.
 */
export function shipMotion(components: readonly WaveComponent[], scale: number, t: number, x: number, z: number, heading: number, out: ShipMotion, shore?: Readonly<ShoreParams>): ShipMotion {
  // Ship-local +z (bow) is world (sin h, cos h); local +x (starboard) is (cos h, -sin h).
  const fx = Math.sin(heading), fz = Math.cos(heading), rx = fz, rz = -fx;
  const at = (px: number, pz: number): number => shore
    ? waterHeight(components, shore, scale, px, pz, t, true) : surfaceHeight(components, scale, px, pz, t, true);
  const centre = at(x, z);
  const bow = at(x + fx * HALF_LENGTH, z + fz * HALF_LENGTH), stern = at(x - fx * HALF_LENGTH, z - fz * HALF_LENGTH);
  const starboard = at(x + rx * HALF_BEAM, z + rz * HALF_BEAM), port = at(x - rx * HALF_BEAM, z - rz * HALF_BEAM);
  out.heave = (centre + bow + stern + starboard + port) / 5;
  // Positive rotation.x lowers the bow; positive rotation.z raises starboard.
  out.pitch = -Math.atan2(bow - stern, 2 * HALF_LENGTH);
  out.roll = Math.atan2(starboard - port, 2 * HALF_BEAM);
  return out;
}

/**
 * A hull with mass on its waterplane: heave, pitch and roll each follow the water's pose
 * (`shipMotion`, the waterplane under the hull) as a damped spring with the given natural period,
 * so the hull lags, overshoots and settles instead of copying the surface. The gap between hull
 * and water is what pushes water about (`hullImmersion`) and slams the bow.
 */
export class HullDynamics {
  readonly pose: ShipMotion = { heave: 0, pitch: 0, roll: 0 };
  private readonly velocity: ShipMotion = { heave: 0, pitch: 0, roll: 0 };
  private settled = false;

  /** Advances by `dt` towards `water`; the first step (and reduced motion) snaps to it. */
  step(dt: number, water: Readonly<ShipMotion>, params: Readonly<HullParams>, snap = false): ShipMotion {
    if (!this.settled || snap) {
      Object.assign(this.pose, water);
      this.velocity.heave = this.velocity.pitch = this.velocity.roll = 0;
      this.settled = true;
      return this.pose;
    }
    const steps = Math.ceil(dt / (1 / 120)), h = dt / Math.max(steps, 1);
    for (let i = 0; i < steps; i++) {
      this.spring("heave", params.heavePeriod, water.heave, params.damping, h);
      this.spring("pitch", params.pitchPeriod, water.pitch, params.damping, h);
      this.spring("roll", params.rollPeriod, water.roll, params.damping, h);
    }
    return this.pose;
  }

  /** Semi-implicit Euler: velocity first, then position. */
  private spring(axis: keyof ShipMotion, period: number, target: number, damping: number, h: number): void {
    const w = 2 * Math.PI / Math.max(period, 0.1);
    this.velocity[axis] += (w * w * (target - this.pose[axis]) - 2 * damping * w * this.velocity[axis]) * h;
    this.pose[axis] += this.velocity[axis] * h;
  }
}

/** How deep (m) the bow and stern sit below the water around them: draft plus the hull–water gap. */
export function hullImmersion(water: Readonly<ShipMotion>, hull: Readonly<ShipMotion>, out: { bow: number; stern: number }): { bow: number; stern: number } {
  const reach = SHIP.length / 2;
  // Positive rotation.x lowers the bow.
  const waterBow = water.heave - Math.sin(water.pitch) * reach, hullBow = hull.heave - Math.sin(hull.pitch) * reach;
  const waterStern = water.heave + Math.sin(water.pitch) * reach, hullStern = hull.heave + Math.sin(hull.pitch) * reach;
  out.bow = Math.max(0, SHIP.draft + waterBow - hullBow);
  out.stern = Math.max(0, SHIP.draft + waterStern - hullStern);
  return out;
}
